import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Play, CheckCircle2, Search, RotateCcw, ClipboardCheck } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading } from "@/components/site/ui";
import { Panel, Th, Td, AdminHeading } from "@/components/admin/ui";

const HK_STATUS_COLOR = {
  Unassigned: "text-muted-foreground bg-white/5", Assigned: "text-pool bg-pool/10",
  Accepted: "text-pool bg-pool/10", "In Progress": "text-amber-300 bg-amber-400/10",
  "Inspection Required": "text-pool bg-pool/10", "Reclean Required": "text-red-300 bg-red-400/10",
  Ready: "text-green-300 bg-green-400/10", Inspected: "text-green-300 bg-green-400/10",
  Cancelled: "text-muted-foreground bg-white/5",
};

export default function AdminHousekeeping() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [f, setF] = useState("");
  const isMgr = ["owner", "host", "resort_manager", "housekeeping_manager"].includes(user?.role);

  const load = () => api.get("/admin/housekeeping", { params: f ? { status: f } : {} })
    .then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [f]);

  const act = async (a, action, reason) => {
    try { await api.post(`/admin/housekeeping/${a.id}/action`, { action, reason }); toast.success("Updated."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const statuses = ["", "Unassigned", "Assigned", "In Progress", "Inspection Required", "Reclean Required", "Ready"];
  return (
    <div>
      <AdminHeading title="Housekeeping" subtitle={`${rows?.length ?? 0} assignments`} />
      <div className="flex gap-2 mb-5 flex-wrap">
        {statuses.map((s) => <button key={s} onClick={() => setF(s)} className={`px-3 py-1.5 rounded-full text-xs border ${f === s ? "bg-pool/15 border-pool/40 text-pool" : "border-white/10 text-muted-foreground"}`}>{s || "All"}</button>)}
      </div>
      <Panel>
        {rows === null ? <Loading /> : rows.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No assignments.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px]">
              <thead><tr><Th>Type</Th><Th>Unit</Th><Th>Priority</Th><Th>Due</Th><Th>Status</Th><Th className="text-right">Actions</Th></tr></thead>
              <tbody>{rows.map((a) => (
                <tr key={a.id} className="hover:bg-white/5" data-testid={`hk-row-${a.id}`}>
                  <Td className="whitespace-nowrap">{a.assignment_type}</Td>
                  <Td className="text-muted-foreground">{a.accommodation_id ? a.accommodation_id.slice(0, 8) : "—"}</Td>
                  <Td>{a.priority}</Td>
                  <Td className="text-muted-foreground">{a.due_at ? a.due_at.slice(0, 10) : "—"}</Td>
                  <Td><span className={`text-[11px] px-2 py-0.5 rounded-full ${HK_STATUS_COLOR[a.status] || "text-muted-foreground"}`}>{a.status}{a.overdue && " · overdue"}</span></Td>
                  <Td className="text-right whitespace-nowrap">
                    {["Assigned", "Accepted"].includes(a.status) && <button title="Start" onClick={() => act(a, "start")} className="text-muted-foreground hover:text-amber-300 p-1.5" data-testid={`hk-start-${a.id}`}><Play size={15} /></button>}
                    {a.status === "In Progress" && <button title="Complete" onClick={() => act(a, "complete")} className="text-muted-foreground hover:text-green-300 p-1.5" data-testid={`hk-complete-${a.id}`}><CheckCircle2 size={15} /></button>}
                    {isMgr && a.status === "Inspection Required" && <>
                      <button title="Inspection pass" onClick={() => act(a, "inspect-pass")} className="text-muted-foreground hover:text-green-300 p-1.5" data-testid={`hk-pass-${a.id}`}><ClipboardCheck size={15} /></button>
                      <button title="Reclean" onClick={() => act(a, "inspect-fail")} className="text-muted-foreground hover:text-red-300 p-1.5" data-testid={`hk-fail-${a.id}`}><RotateCcw size={15} /></button>
                    </>}
                  </Td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
